import READ_AVERAGES from "@app/analytics/constants/readAverages";
import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import * as Filters from "@app/analytics/meetops/filters";
import { DateRange, MeetOpsQuery } from "@app/analytics/meetops/types";
import { QueryMetric } from "@app/analytics/meetops/types";
import {
  ParsedQueryResultRequiredBy,
  parseMetricsDataFromQueryResult,
} from "@app/analytics/utils";
import { useWorkspaceOverview } from "@app/analytics/WorkspaceOverview/WorkspaceOverviewProvider";

export type MeetingManagerMetric = "meetingScore" | "sentiment" | "engagement";

const MEETING_MANAGER_METRICS: QueryMetric[] = [
  "meetingScore",
  "sentiment",
  "engagement",
];

export type MeetingManagerResult =
  ParsedQueryResultRequiredBy<MeetingManagerMetric>;

const getQuery = ({
  dateRanges,
}: {
  dateRanges: DateRange[];
}): MeetOpsQuery => {
  return {
    dimensions: [],
    dateRanges,
    metrics: MEETING_MANAGER_METRICS,
    filters: [Filters.multiParticipantMeeting()],
    people: "everyone",
  };
};

const DEFAULT_RESULT: MeetingManagerResult = {
  meetingScore: { value: null, formattedValue: "-" },
  sentiment: { value: null, formattedValue: "-" },
  engagement: { value: null, formattedValue: "-" },
};

const useMeetingManager = () => {
  const { queryDateRanges } = useWorkspaceOverview();

  const workspaceAverageQuery = useMetricsQuery(
    getQuery({
      dateRanges: queryDateRanges,
    }),
    {
      queryKey: ["meetingManagerWorkspaceAverage"],
      staleTime: 30 * 60 * 1000,
    }
  );

  const refetchAll = () => {
    if (workspaceAverageQuery.isError) workspaceAverageQuery.refetch();
  };

  const [workspaceAverages] =
    parseMetricsDataFromQueryResult<MeetingManagerResult>(
      workspaceAverageQuery.data,
      DEFAULT_RESULT
    );

  return {
    isLoading: workspaceAverageQuery.isLoading,
    isError: workspaceAverageQuery.isError,
    refetch: refetchAll,
    workspaceAverages,
    readAverages: READ_AVERAGES,
  };
};

export default useMeetingManager;
